import * as React from 'react';
import {
    ChecklistItem,
    ChecklistItemPlacement,
    ChecklistItemReleaseTypes,
    ChecklistItemTemplate,
    ChecklistItemType,
    DefaultUserGroupId,
    RelativeTimeTrigger,
    ReleaseStepType,
    RunwayResponseType,
    SubscriptionFeatureType,
} from '../types';
import Modal from 'antd/lib/modal/Modal';
import { checklistItemTypeForPlacement, isStepChecklistPlacement } from '../helpers';
import { Nullable, OrgComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import { formValidAndRequiredFieldsNotEmpty } from '../helpers/formHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import {
    ReleaseStepNewChecklistItemRequest,
    ReleaseStepUpdateChecklistItemRequest,
    tryUpdateAppSettingsV2,
} from '../actions/appSettings';
import { AppSettingsV2Updatable } from '../actions/updatable';
import {
    displayTextForChecklistPlacement,
    displayTextForReleaseStepType,
    displayTextForUserChecklistGroup,
} from '../helpers/displayHelpers';
import Select from 'antd/lib/select';
import useForm from 'antd/lib/form/hooks/useForm';
import Form, { useWatch } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Input from 'antd/lib/input';
import {
    selectApp,
    selectApprovalItem,
    selectAppStoreType,
    selectChecklistItem,
    selectOrgGroups,
    selectRegressionItem,
    selectStepChecklistItem,
    selectStepChecklistItemForStepRun,
    selectStepConfigDataFromStepGraphs,
    selectWorkflowEnabledSteps,
    selectIsWorkflowsCIStepEnabled,
    selectOrgLevelChecklistItem,
    selectOrgUsers,
} from '../selectors/selectors';
import MarkdownEditor from './HOCs/MarkdownEditor';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import { antGrey, runwayColors } from './styles/globalStyles';
import Radio from 'antd/lib/radio';
import { globalMessage } from '../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import {
    CreateChecklistItemRequest,
    StepRunUpdatable,
    UpdateChecklistItemRequest,
    UpdateOrgChecklistItemTemplateRequest,
} from '@/actions/stepRun';
import {
    CreateChecklistItemTemplateRequest,
    StepConfigUpdatable,
    UpdateChecklistItemTemplateRequest,
} from '@/actions/stepConfig';
import { InlineStepConfigDescription } from '@/components/common/InlineStepConfigDescription';
import { InfoCircleOutlined } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import { RelativeTimeTriggerSettings } from './RelativeTimeTriggerSettings';
import { OrgUpdatable } from '@/actions/organization';

enum OwnerType {
    ApproverGroups = 'approverGroups',
    IndividualOwners = 'owners',
}

interface CreateEditChecklistItemProps extends OrgComponentProps {
    open: boolean;
    releaseStepTypes: ReleaseStepType[];
    placement: ChecklistItemPlacement;
    onCancel: () => void;
    onSuccess: () => void;
    isEditingItemWithId?: string;
    releaseId?: string;
    defaultReleaseStepTypeOrStepConfigId?: ReleaseStepType | string;
    releaseVersion?: string; // passed when creating a CLI from a release, used to allow for one-off CLIs on the release
    displayableReleaseVersion?: string;
    onRolloutMonitoringNeedsRefresh?: () => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    releaseStepType: ReleaseStepType;
    appId?: string;
    isOrgLevelTemplate: boolean;
}

const CreateUpdateChecklistItemModal: React.FC<CreateEditChecklistItemProps> = (
    props: CreateEditChecklistItemProps,
) => {
    const { Option } = Select;
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();
    const releaseStepTypeOrStepConfigId = useWatch('releaseStepTypeOrStepConfigId', form);
    const isOneOffForVersion = useWatch('oneOffForVersion', form);
    const isTargetCompletionSettingsOpen = useWatch(['targetCompletion', 'enabled'], form);
    const isIncompleteReminderEnabled = useWatch('incompleteReminderEnabled', form);
    const isAutocompleteAtTargetCompletionEnabled = useWatch('autocompleteAtTargetCompletionEnabled', form);
    const description = useWatch('description', form);

    const dispatch = useDispatch();

    const [ownerType, setOwnerType] = React.useState(OwnerType.ApproverGroups);

    const isStepChecklistItem = isStepChecklistPlacement(props.placement);

    let releaseStepTypes = props.releaseStepTypes;
    if (!releaseStepTypes.includes(ReleaseStepType.Rollout)) {
        releaseStepTypes = [...releaseStepTypes, ReleaseStepType.Rollout];
    }

    const workflowId = props.appId ? useSelector((state: RootState) => selectApp(state, props.appId).workflowId) : null;

    const workflowEnabledSteps =
        props.appId ? useSelector((state: RootState) => selectWorkflowEnabledSteps(state, props.appId)) : [];

    // Selectors
    const isEditingItem = useSelector((state: RootState) => {
        if (props.isEditingItemWithId) {
            switch (props.placement) {
                case ChecklistItemPlacement.StepChecklist: {
                    if (props.appId) {
                        // first attempt to get the app CLI. if not present, can assume the CLI is one-off for this release, so just return the step's CLI
                        const appSettingsChecklistItem = selectChecklistItem(
                            state,
                            props.appId,
                            props.isEditingItemWithId,
                        );

                        if (appSettingsChecklistItem) {
                            return appSettingsChecklistItem;
                        } else if (props.releaseId && props.defaultReleaseStepTypeOrStepConfigId) {
                            const stepChecklistItem =
                                props.stepRunComponentProps ?
                                    selectStepChecklistItemForStepRun(
                                        state,
                                        props.stepRunComponentProps.stepRunId,
                                        props.isEditingItemWithId,
                                    )
                                :   selectStepChecklistItem(
                                        state,
                                        props.releaseId,
                                        props.defaultReleaseStepTypeOrStepConfigId as ReleaseStepType,
                                        props.isEditingItemWithId,
                                    );

                            return stepChecklistItem;
                        } else {
                            return null;
                        }
                    } else {
                        const orgSettingsChecklistItem = selectOrgLevelChecklistItem(state, props.isEditingItemWithId);

                        if (orgSettingsChecklistItem) {
                            return orgSettingsChecklistItem;
                        }
                        return null;
                    }
                }
                case ChecklistItemPlacement.Approvals:
                    return selectApprovalItem(
                        state,
                        props.releaseId,
                        props.isEditingItemWithId,
                        props.stepRunComponentProps?.stepRunId,
                    );
                case ChecklistItemPlacement.RegressionTesting:
                    return selectRegressionItem(
                        state,
                        props.releaseId,
                        props.isEditingItemWithId,
                        props.stepRunComponentProps?.stepRunId,
                    );
            }
        }

        return null;
    });

    const isWorkflowsEnabled = !!workflowId;

    const hasStepConfigId = releaseStepTypeOrStepConfigId && !releaseStepTypes.includes(releaseStepTypeOrStepConfigId);
    const shouldModifyChecklistTemplate = isWorkflowsEnabled && hasStepConfigId;
    const [isWorkflowsEnabledStep, setIsWorkflowsEnabledStep] = React.useState(
        isWorkflowsEnabled && workflowEnabledSteps[props.releaseStepType],
    );
    React.useEffect(() => {
        const isReleaseStepType = Object.values(ReleaseStepType).includes(releaseStepTypeOrStepConfigId);
        setIsWorkflowsEnabledStep(isReleaseStepType ? !!workflowEnabledSteps[releaseStepTypeOrStepConfigId] : true);
    }, [releaseStepTypeOrStepConfigId, isWorkflowsEnabledStep]);

    const shouldModifyChecklistOneOff =
        !!props.stepRunComponentProps &&
        (isOneOffForVersion ||
            ((isEditingItem as ChecklistItem)?.stepRunId && !(isEditingItem as ChecklistItem).templateId));

    const stepGraphStepsMap =
        props.appId ? useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, props.appId)) : [];

    const [
        createStepRunChecklistItem,
        createStepRunChecklistItemIsLoading,
        createStepRunChecklistItemResponseType,
        createStepRunChecklistItemError,
    ] = useCallUpdatable(StepRunUpdatable.CreateStepRunChecklistItem, props.stepRunComponentProps);

    const [
        createChecklistItemTemplate,
        createChecklistItemTemplateIsLoading,
        createChecklistItemTemplateResponseType,
        createChecklistItemTemplateError,
    ] = useCallUpdatable(StepConfigUpdatable.CreateChecklistItemTemplate, {
        orgId: props.orgId,
        workflowId: workflowId,
        stepConfigId: releaseStepTypeOrStepConfigId,
    });

    const [
        updateChecklistItemOrgTemplate,
        updateChecklistItemOrgTemplateIsLoading,
        updateChecklistItemOrgTemplateResponseType,
        updateChecklistItemOrgTemplateError,
    ] = useCallUpdatable(OrgUpdatable.UpdateOrgChecklistItemTemplate, { orgId: props.orgId });

    const [
        createChecklistItemOrgTemplate,
        createChecklistItemOrgTemplateIsLoading,
        createChecklistItemOrgTemplateResponseType,
        createChecklistItemOrgTemplateError,
    ] = useCallUpdatable(OrgUpdatable.CreateOrgChecklistItemTemplate, { orgId: props.orgId });

    const releaseStepNewChecklistItemSaveIsLoading = useIsLoading(AppSettingsV2Updatable.NewChecklistItem);
    const releaseStepNewChecklistItemSaveCompleted = useUpdated(AppSettingsV2Updatable.NewChecklistItem);
    const releaseStepNewChecklistItemError = useError(AppSettingsV2Updatable.NewChecklistItem);

    const newChecklistItemSaveIsLoading =
        props.isOrgLevelTemplate ? createChecklistItemOrgTemplateIsLoading
        : shouldModifyChecklistOneOff ? createStepRunChecklistItemIsLoading
        : shouldModifyChecklistTemplate ? createChecklistItemTemplateIsLoading
        : releaseStepNewChecklistItemSaveIsLoading;
    const newChecklistItemSaveCompleted =
        props.isOrgLevelTemplate ? createChecklistItemOrgTemplateResponseType === RunwayResponseType.Success
        : shouldModifyChecklistOneOff ? createStepRunChecklistItemResponseType === RunwayResponseType.Success
        : shouldModifyChecklistTemplate ? createChecklistItemTemplateResponseType === RunwayResponseType.Success
        : releaseStepNewChecklistItemSaveCompleted;
    const newChecklistItemError =
        props.isOrgLevelTemplate ? createChecklistItemOrgTemplateError
        : shouldModifyChecklistOneOff ? createStepRunChecklistItemError
        : shouldModifyChecklistTemplate ? createChecklistItemTemplateError
        : releaseStepNewChecklistItemError;

    const platform =
        props.appId ? useSelector((state: RootState) => selectApp(state, props.appId).appSettings.platform) : null;
    const appStoreType = props.appId ? useSelector((state: RootState) => selectAppStoreType(state, props.appId)) : null;

    const checklistItem = isEditingItem as ChecklistItem;

    const [
        updateStepRunChecklistItem,
        updateStepRunChecklistItemIsLoading,
        updateStepRunChecklistItemResponseType,
        updateStepRunChecklistItemError,
    ] = useCallUpdatable(
        StepRunUpdatable.UpdateStepRunChecklistItem,
        {
            checklistItemId: props.isEditingItemWithId,
            ...props.stepRunComponentProps,
        },
        props.isEditingItemWithId,
    );

    const [
        updateChecklistItemTemplate,
        updateChecklistItemTemplateIsLoading,
        updateChecklistItemTemplateResponseType,
        updateChecklistItemTemplateError,
    ] = useCallUpdatable(StepConfigUpdatable.UpdateChecklistItemTemplate, {
        orgId: props.orgId,
        workflowId: workflowId,
        stepConfigId: releaseStepTypeOrStepConfigId,
        checklistItemId: checklistItem?.templateId ?? checklistItem?.uid,
    });

    const releaseStepIsUpdateLoading = useIsLoading(AppSettingsV2Updatable.UpdateReleaseStepChecklistItem);
    const releaseStepIsUpdated = useUpdated(AppSettingsV2Updatable.UpdateReleaseStepChecklistItem);
    const releaseStepUpdateError = useError(AppSettingsV2Updatable.UpdateReleaseStepChecklistItem);

    const isUpdateLoading =
        props.isOrgLevelTemplate ? updateChecklistItemOrgTemplateIsLoading
        : shouldModifyChecklistOneOff ? updateStepRunChecklistItemIsLoading
        : shouldModifyChecklistTemplate ? updateChecklistItemTemplateIsLoading
        : releaseStepIsUpdateLoading;
    const isUpdated =
        props.isOrgLevelTemplate ? updateChecklistItemOrgTemplateResponseType === RunwayResponseType.Success
        : shouldModifyChecklistOneOff ? updateStepRunChecklistItemResponseType === RunwayResponseType.Success
        : shouldModifyChecklistTemplate ? updateChecklistItemTemplateResponseType === RunwayResponseType.Success
        : releaseStepIsUpdated;
    const updateError =
        props.isOrgLevelTemplate ? updateChecklistItemOrgTemplateError
        : shouldModifyChecklistOneOff ? updateStepRunChecklistItemError
        : shouldModifyChecklistTemplate ? updateChecklistItemTemplateError
        : releaseStepUpdateError;

    const users =
        props.appId ?
            useSelector((state: RootState) => selectAppOrgUsers(state, props.appId))
        :   useSelector((state: RootState) => selectOrgUsers(state, props.orgId));

    // special case for editing a CLI that has pilot as an approver role and has been set with the current release's pilot as an owner
    const isEditingItemWithPilotApproverGroup =
        props.releaseId &&
        isEditingItem &&
        isEditingItem.approverGroups.includes(DefaultUserGroupId.Pilot) &&
        isEditingItem.owners.length > 0;

    // Functions

    const createOrUpdateChecklistItem = (
        title: string,
        notifyEnabled: boolean,
        releaseTypes: ChecklistItemReleaseTypes,
        placement: ChecklistItemPlacement,
        approverGroups: string[],
        owners: string[],
        incompleteReminderEnabled: boolean,
        autocompleteAtTargetCompletionEnabled: boolean,
        includeDescriptionInNotificationsEnabled: boolean,
        releaseStepType?: ReleaseStepType,
        description?: string,
        editingItemId?: string,
        type?: ChecklistItemType,
        oneOffForVersion?: string,
        targetCompletion?: RelativeTimeTrigger,
    ) => {
        if (editingItemId) {
            const requestData: ReleaseStepUpdateChecklistItemRequest = {
                orgId: props.orgId,
                appId: props.appId,
                checklistItemId: editingItemId,
                title,
                notifyEnabled,
                description,
                approverGroups: approverGroups,
                owners,
                releaseTypes,
                oneOffForVersion,
                targetCompletion,
                incompleteReminderEnabled,
                autocompleteAtTargetCompletionEnabled,
                includeDescriptionInNotificationsEnabled,
            };

            if (props.isOrgLevelTemplate) {
                const data: UpdateOrgChecklistItemTemplateRequest = {
                    ...requestData,
                    type,
                    placement,
                    checklistItemId: props.isEditingItemWithId,
                };
                updateChecklistItemOrgTemplate(data);
            } else if (shouldModifyChecklistOneOff) {
                const data: UpdateChecklistItemRequest = {
                    ...requestData,
                    type,
                    placement,
                };
                updateStepRunChecklistItem(data);
            } else if (shouldModifyChecklistTemplate) {
                const data: UpdateChecklistItemTemplateRequest = {
                    ...requestData,
                    type,
                    placement,
                    originWorkflowRunId: props.stepRunComponentProps?.workflowRunId,
                };
                updateChecklistItemTemplate(data);
            } else {
                dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdateReleaseStepChecklistItem, requestData));
            }
        } else {
            const requestData: ReleaseStepNewChecklistItemRequest = {
                orgId: props.orgId,
                appId: props.appId,
                title,
                notifyEnabled,
                type,
                placement,
                releaseStepType,
                description,
                approverGroups: approverGroups,
                owners,
                releaseTypes,
                oneOffForVersion,
                releaseId: props.releaseId,
                targetCompletion,
                incompleteReminderEnabled,
                autocompleteAtTargetCompletionEnabled,
                includeDescriptionInNotificationsEnabled,
            };

            if (props.isOrgLevelTemplate) {
                createChecklistItemOrgTemplate(requestData);
            } else if (shouldModifyChecklistOneOff) {
                const data: CreateChecklistItemRequest = {
                    ...requestData,
                };
                createStepRunChecklistItem(data);
            } else if (shouldModifyChecklistTemplate) {
                const data: CreateChecklistItemTemplateRequest = {
                    ...requestData,
                    originWorkflowRunId: props.stepRunComponentProps?.workflowRunId,
                };
                createChecklistItemTemplate(data);
            } else {
                dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.NewChecklistItem, requestData));
            }
        }
    };
    const onFieldsChange = () => {
        const requiredFields = ['title'];

        if (isStepChecklistItem && !shouldModifyChecklistOneOff) {
            requiredFields.push('releaseStepTypeOrStepConfigId');
        }
        const isValid = formValidAndRequiredFieldsNotEmpty(form, requiredFields);
        setFormIsValid(isValid);
    };

    const onFinish = () => {
        form.validateFields()
            .then((values) => {
                const description = values.description;
                const title = values.title;
                const approverGroups = values.approverGroups;
                const owners = values.owners;
                const editingItemId = props.isEditingItemWithId;
                const releaseTypes = values.releaseTypes;
                const notifyEnabled = values.notifyEnabled;
                const placement = props.placement;
                const itemType = checklistItemTypeForPlacement(props.placement);
                const oneOffForVersion = values.oneOffForVersion === true ? props.releaseVersion : null;
                const incompleteReminderEnabled = values.incompleteReminderEnabled;
                const autocompleteAtTargetCompletionEnabled = values.autocompleteAtTargetCompletionEnabled;
                const includeDescriptionInNotificationsEnabled = values.includeDescriptionInNotificationsEnabled;
                const targetCompletion = values.targetCompletion;

                createOrUpdateChecklistItem(
                    title,
                    notifyEnabled,
                    releaseTypes,
                    placement,
                    approverGroups,
                    owners,
                    incompleteReminderEnabled,
                    autocompleteAtTargetCompletionEnabled,
                    includeDescriptionInNotificationsEnabled,
                    values.releaseStepTypeOrStepConfigId,
                    description,
                    editingItemId,
                    itemType,
                    oneOffForVersion,
                    targetCompletion,
                );
            })
            .catch((error) => {
                globalMessage.error(error);

                setFormIsValid(false);
            });
    };

    const handleOwnerSelections = (field) => {
        const fieldValues = form.getFieldValue(field);

        const otherField = field === OwnerType.ApproverGroups ? OwnerType.IndividualOwners : OwnerType.ApproverGroups;
        const otherFieldValues = form.getFieldValue(otherField);

        // //if user set values for field, remove selections from other field
        if (fieldValues?.length > 0 && otherFieldValues?.length > 0) {
            form.setFieldsValue({ [otherField]: [] });
        }
    };

    const isWorkflowsCIStepEnabled =
        props.appId ? useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, props.appId)) : true;

    const placementText = displayTextForChecklistPlacement(props.placement).toLocaleLowerCase();
    const title = isEditingItem ? `Edit ${placementText} item` : `New ${placementText} item`;
    const buttonTitle = isEditingItem ? `Update ${placementText} item` : `Create ${placementText} item`;
    const helperText = `${
        isEditingItem ? 'Changes to t' : 'T'
    }his checklist item will apply to all releases for the app.`;
    const allGroups = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const isEditing = !!isEditingItem;
    const releaseStepTypeShouldFocus = !isEditing && !props.defaultReleaseStepTypeOrStepConfigId && isStepChecklistItem;
    const checklistNameShouldFocus = !!props.defaultReleaseStepTypeOrStepConfigId;

    const tooltipHelpStyling: React.CSSProperties = {
        paddingLeft: 0,
        fontSize: '12px',
        paddingBottom: 0,
        marginBottom: 0,
        marginRight: 0,
    };

    const bulletPointHelpStyling: React.CSSProperties = {
        paddingLeft: 16,
        fontSize: '12px',
        paddingBottom: 0,
        marginBottom: 0,
        marginTop: 10,
        color: runwayColors.statusGrey,
    };

    const initialValues =
        isEditingItem ?
            {
                releaseStepTypeOrStepConfigId:
                    isEditingItem.releaseStepType ??
                    (isEditingItem as ChecklistItemTemplate).stepConfigId ??
                    props.stepRunComponentProps?.stepConfigId ??
                    props.defaultReleaseStepTypeOrStepConfigId,
                title: isEditingItem.title ?? null,
                description: isEditingItem.description ?? '',
                approverGroups: isEditingItem.approverGroups,
                // do not populate owners for cases where approver group is pilot and we're editing a release's CLI
                owners: isEditingItemWithPilotApproverGroup ? [] : isEditingItem.owners,
                notifyEnabled: isEditingItem.notifyEnabled ?? true,
                releaseTypes: isEditingItem.releaseTypes ?? ChecklistItemReleaseTypes.All,
                oneOffForVersion: !!isEditingItem.oneOffForVersion || shouldModifyChecklistOneOff,
                incompleteReminderEnabled: isEditingItem?.incompleteReminderEnabled,
                autocompleteAtTargetCompletionEnabled: isEditingItem?.autocompleteAtTargetCompletionEnabled,
                includeDescriptionInNotificationsEnabled: isEditingItem?.includeDescriptionInNotificationsEnabled,
                targetCompletion: isEditingItem?.targetCompletion ?? { enabled: false },
            }
        :   {
                releaseStepTypeOrStepConfigId: props.defaultReleaseStepTypeOrStepConfigId ?? null,
                releaseTypes: ChecklistItemReleaseTypes.All,
                title: null,
                description: null,
                approverGroups: [DefaultUserGroupId.Pilot],
                owners: [],
                notifyEnabled: true,
                oneOffForVersion: false,
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                targetCompletion: { enabled: false },
            };

    const hasNonPilotOwners =
        initialValues.owners?.length > 0 && !initialValues.approverGroups.includes(DefaultUserGroupId.Pilot);

    // Effects
    React.useEffect(() => {
        form.setFieldsValue({ releaseStepType: props.defaultReleaseStepTypeOrStepConfigId ?? null });
    }, [props.defaultReleaseStepTypeOrStepConfigId]);

    React.useEffect(() => {
        if (newChecklistItemSaveCompleted) {
            globalMessage.success('New checklist item added!');

            props.onSuccess();
        }

        if (props.onRolloutMonitoringNeedsRefresh) {
            props.onRolloutMonitoringNeedsRefresh();
        }
    }, [newChecklistItemSaveCompleted]);

    React.useEffect(() => {
        if (newChecklistItemError) {
            globalMessage.error(newChecklistItemError);
        }
    }, [newChecklistItemError]);

    React.useEffect(() => {
        if (isUpdated) {
            globalMessage.success('Updated!');

            props.onSuccess();
        }

        if (props.onRolloutMonitoringNeedsRefresh) {
            props.onRolloutMonitoringNeedsRefresh();
        }
    }, [isUpdated]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    React.useEffect(() => {
        if (props.open) {
            form.setFieldsValue(initialValues);

            if (hasNonPilotOwners) {
                // set radio to individual owners
                form.setFieldValue('ownerType', OwnerType.IndividualOwners);
                setOwnerType(OwnerType.IndividualOwners);
            }
        }
    }, [props.open]);

    return (
        <Modal
            title={title}
            open={props.open}
            onCancel={props.onCancel}
            onOk={() => form.submit()}
            okText={buttonTitle}
            confirmLoading={newChecklistItemSaveIsLoading || isUpdateLoading}
            okButtonProps={{ disabled: !formIsValid }}
            afterClose={() => {
                form.resetFields();
                setFormIsValid(false);
                setOwnerType(OwnerType.ApproverGroups);
            }}
            destroyOnClose={true}
            width="70%"
            style={{ minWidth: 500, maxWidth: 800, top: 48 }}
        >
            <Form
                form={form}
                name="newChecklistItem"
                onFinish={onFinish}
                onFieldsChange={onFieldsChange}
                autoComplete="off"
                layout="vertical"
                initialValues={initialValues}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%', columnGap: 24 }}>
                    <FormItem
                        hidden={!isStepChecklistItem}
                        label="Release step"
                        name="releaseStepTypeOrStepConfigId"
                        rules={[{ required: true }]}
                        style={{ minWidth: 280, width: '100%', flex: 1 }}
                    >
                        <Select placeholder="Select" disabled={isEditing} autoFocus={releaseStepTypeShouldFocus}>
                            {releaseStepTypes
                                .filter((releaseStepType) => {
                                    if (
                                        isWorkflowsEnabled &&
                                        workflowEnabledSteps[releaseStepType] &&
                                        Object.values(stepGraphStepsMap).some(
                                            (stepGraphStep) => stepGraphStep.archetype === releaseStepType,
                                        )
                                    ) {
                                        // filter any migrated release steps only if stepGraphStepsMap contains at least one step with that  archetype
                                        return false;
                                    }

                                    return true;
                                })
                                .map((releaseStepType, index) => {
                                    return (
                                        <Option key={index} value={releaseStepType}>
                                            {displayTextForReleaseStepType(
                                                releaseStepType,
                                                platform,
                                                appStoreType,
                                                isWorkflowsCIStepEnabled,
                                            )}
                                        </Option>
                                    );
                                })}
                            {Object.entries(stepGraphStepsMap)
                                .filter(([, stepGraphStep]) => {
                                    if (isWorkflowsEnabled) {
                                        // only show release steps that are migrated
                                        return workflowEnabledSteps[stepGraphStep.archetype];
                                    }
                                    return false;
                                })
                                .map(([stepConfigId, stepGraphStep]) => {
                                    return (
                                        <Option key={stepConfigId} value={stepConfigId}>
                                            <InlineStepConfigDescription
                                                stepGraphStep={stepGraphStep}
                                                platform={platform}
                                                appStoreType={appStoreType}
                                                isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                            />
                                        </Option>
                                    );
                                })}
                        </Select>
                    </FormItem>
                    <FormItem
                        label="Release types"
                        name="releaseTypes"
                        style={{ minWidth: 280, width: '100%', flex: 1 }}
                    >
                        <Select>
                            <Option key="all" value={ChecklistItemReleaseTypes.All}>
                                All types
                            </Option>
                            <Option key="allExceptHotfix" value={ChecklistItemReleaseTypes.AllExceptHotfixAndRollback}>
                                All types except hotfixes and rollbacks
                            </Option>
                            <Option key="hotfix" value={ChecklistItemReleaseTypes.HotfixOnly}>
                                Hotfixes only
                            </Option>
                            <Option key="rollback" value={ChecklistItemReleaseTypes.RollbackOnly}>
                                Rollbacks only
                            </Option>
                        </Select>
                    </FormItem>
                </div>
                <FormItem label="Title" name="title" rules={[{ required: true }]}>
                    <Input
                        placeholder="e.g. Update Confluence with latest details"
                        autoFocus={checklistNameShouldFocus}
                        autoComplete="off"
                    />
                </FormItem>
                {props.releaseVersion && (
                    <div style={{ marginBottom: 10 }}>
                        <FormItem
                            name="oneOffForVersion"
                            rules={[{ required: true }]}
                            valuePropName="checked"
                            style={{ marginBottom: 0 }}
                        >
                            <Checkbox disabled={!!isEditingItem && shouldModifyChecklistOneOff}>
                                Include this checklist item for this release ({props.displayableReleaseVersion}) only
                            </Checkbox>
                        </FormItem>
                    </div>
                )}
                <>
                    <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center' }}>
                        <div>
                            <Tooltip>
                                <FormItem
                                    name={['targetCompletion', 'enabled']}
                                    valuePropName="checked"
                                    style={{ padding: 0, margin: 0 }}
                                >
                                    <Checkbox> Set a target completion time </Checkbox>
                                </FormItem>
                            </Tooltip>
                        </div>
                        <Tooltip
                            overlayInnerStyle={{ marginRight: 0 }}
                            title={
                                <ul style={tooltipHelpStyling}>
                                    Set a target completion time relative to lifecycle events.
                                </ul>
                            }
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                        </Tooltip>
                    </div>
                    {isTargetCompletionSettingsOpen && (
                        <div style={{ marginBottom: 6 }}>
                            <RelativeTimeTriggerSettings
                                parentFieldName="targetCompletion"
                                orgId={props.orgId}
                                appId={props.appId}
                                platform={platform}
                                stepConfigId={props.stepRunComponentProps?.stepConfigId}
                                initialValues={initialValues.targetCompletion}
                                isOrgLevelItem={props.isOrgLevelTemplate}
                            />
                            <div style={{ marginTop: 12, marginBottom: 10, display: 'flex', alignItems: 'center' }}>
                                <div>
                                    <Tooltip>
                                        <FormItem
                                            name="autocompleteAtTargetCompletionEnabled"
                                            valuePropName="checked"
                                            noStyle
                                        >
                                            <Checkbox>Auto-complete at target completion time</Checkbox>
                                        </FormItem>
                                    </Tooltip>
                                </div>
                                <Tooltip
                                    overlayInnerStyle={{ marginRight: 0 }}
                                    title={
                                        <ul style={tooltipHelpStyling}>
                                            Runway will automatically update status to 'Completed' at the target
                                            completion time. Use this option with 'Send a reminder' for timed reminders
                                            where no action is needed.
                                        </ul>
                                    }
                                >
                                    <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                                </Tooltip>
                            </div>
                            <div style={{ marginTop: 0, marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                                <div>
                                    <Tooltip>
                                        <FormItem name="incompleteReminderEnabled" valuePropName="checked" noStyle>
                                            <Checkbox>
                                                {isAutocompleteAtTargetCompletionEnabled ?
                                                    'Send a reminder'
                                                :   'Send a reminder if incomplete'}
                                            </Checkbox>
                                        </FormItem>
                                    </Tooltip>
                                </div>
                                <Tooltip
                                    overlayInnerStyle={{ marginRight: 0 }}
                                    title={
                                        <ul style={tooltipHelpStyling}>
                                            {isAutocompleteAtTargetCompletionEnabled ?
                                                'Sent at the target completion time'
                                            :   'Sent if the target completion time has passed and the checklist item has not yet been marked as complete.'
                                            }
                                        </ul>
                                    }
                                >
                                    <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                                </Tooltip>
                            </div>
                            <FormItem
                                shouldUpdate={(prev, curr) => prev.reminderEnabled !== curr.reminderEnabled}
                                noStyle
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('incompleteReminderEnabled') ?
                                        <ul style={bulletPointHelpStyling}>
                                            <li>
                                                Requires 'Checklist item is due' notification to be enabled in
                                                Notification settings.
                                            </li>
                                        </ul>
                                    :   null
                                }
                            </FormItem>

                            {isIncompleteReminderEnabled && (
                                <div
                                    style={{
                                        marginTop: 0,
                                        fontWeight: 'lighter',
                                        fontSize: 12,
                                        paddingLeft: 14,
                                        marginLeft: 0,
                                        color: antGrey.grey7,
                                    }}
                                ></div>
                            )}
                        </div>
                    )}
                </>

                <div style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center' }}>
                    <div>
                        <Tooltip>
                            <FormItem name="notifyEnabled" valuePropName="checked" style={{ padding: 0, margin: 0 }}>
                                <Checkbox>Send a completion notification</Checkbox>
                            </FormItem>
                        </Tooltip>
                    </div>
                    <Tooltip
                        overlayInnerStyle={{ marginRight: 0 }}
                        title={
                            <ul style={tooltipHelpStyling}>
                                Requires 'Checklist item completed' notification to be enabled in Notification settings.
                            </ul>
                        }
                    >
                        <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                    </Tooltip>
                </div>
                <FormItem style={{ paddingTop: 10 }} label="Additional information" name="description">
                    <MarkdownEditor autoFocus={false} />
                </FormItem>
                <div style={{ marginBottom: 16 }}>
                    <FormItem
                        name="includeDescriptionInNotificationsEnabled"
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                    >
                        <Checkbox disabled={!description || description == ''}>
                            Include 'Additional information' in notifications
                        </Checkbox>
                    </FormItem>
                </div>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.CLIOwnership}>
                    {(isDisabled) => (
                        <>
                            <FormItem
                                label={
                                    <PlanRestrictedFeatureWrapper
                                        feature={SubscriptionFeatureType.CLIOwnership}
                                        requiresTooltip
                                    >
                                        <Text disabled={isDisabled}>Assign checklist item ownership by:</Text>
                                    </PlanRestrictedFeatureWrapper>
                                }
                                style={{ maxWidth: 320, marginBottom: 10 }}
                                name={'ownerType'}
                            >
                                <Radio.Group
                                    disabled={isDisabled}
                                    defaultValue={'approverGroups'}
                                    onChange={(e) => setOwnerType(e.target.value)}
                                >
                                    <Radio value={OwnerType.ApproverGroups}>User group</Radio>
                                    <Radio value={OwnerType.IndividualOwners}>User</Radio>
                                </Radio.Group>
                            </FormItem>
                            <FormItem
                                label="Owners"
                                hidden={ownerType === OwnerType.IndividualOwners}
                                name={OwnerType.ApproverGroups}
                                style={{ maxWidth: 320 }}
                            >
                                <Select
                                    disabled={isDisabled}
                                    allowClear
                                    mode="multiple"
                                    placeholder="No owners"
                                    onChange={() => handleOwnerSelections(OwnerType.ApproverGroups)}
                                    filterOption={(input, option) => {
                                        const groupId = option?.value;
                                        if (!groupId) {
                                            return false;
                                        }
                                        const group = allGroups[groupId];
                                        return group?.name.toLowerCase().includes(input.toLowerCase()) ?? false;
                                    }}
                                >
                                    {Object.keys(allGroups).map((userGroupId, index) => {
                                        const group = allGroups[userGroupId];
                                        return (
                                            <Option value={userGroupId} key={index}>
                                                {displayTextForUserChecklistGroup(group)}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormItem>
                            <FormItem
                                label="Owners"
                                name={OwnerType.IndividualOwners}
                                hidden={ownerType === OwnerType.ApproverGroups}
                                style={{ maxWidth: 320 }}
                            >
                                <Select
                                    disabled={isDisabled}
                                    allowClear
                                    mode="multiple"
                                    placeholder="No owners"
                                    onChange={() => handleOwnerSelections(OwnerType.IndividualOwners)}
                                >
                                    {users?.map((user, index) => {
                                        return (
                                            <Option value={user.email} key={index}>
                                                {user.email}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormItem>
                        </>
                    )}
                </PlanRestrictedFeatureWrapper>
            </Form>
            <Space direction="vertical" size={'middle'}>
                {!isOneOffForVersion && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {helperText}
                    </Text>
                )}
                {isEditingItem?.uid && (
                    <Space>
                        <Text type="secondary">Checklist item identifier: </Text>
                        <Text
                            style={{
                                backgroundColor: antGrey.grey5,
                                color: runwayColors.darkTextGrey,
                                borderRadius: 3,
                                borderStyle: 'solid',
                                borderColor: antGrey.grey7,
                                borderWidth: 1,
                                padding: '2px 4px',
                            }}
                        >
                            {checklistItem?.templateId ? checklistItem.templateId : checklistItem.uid}
                        </Text>
                    </Space>
                )}
            </Space>
        </Modal>
    );
};

export default CreateUpdateChecklistItemModal;
